import { fromSpecific, scope, TypedNode } from '@rehaje/core'
import { reactive } from '@rehaje/reactive'
import { isEmpty, nextTick, pDebounceThrottle, toArr } from '@rehaje/shared'

import { MustacheNode } from '../Mustache'
import { EventTriggerEvent } from './event'

import type { AnyActionNode, AnyActionSpecific } from '../Action/TypedNode'
import type { AnyExpressionSpecific } from '../Mustache'
import type { EventPlugin } from './plugin'
import type { TypedSpecificLike, TypedVariableDeclaration } from '@rehaje/core'
import type { PromiseDebounceThrottleOptions, TRecord } from '@rehaje/shared'

export interface EventResult {
  action?: (() => Promise<void>)[] | (() => Promise<void>)
  condition?: boolean
  debounceTime?: number
  throttleTime?: number
}

export interface EventSpecific extends TypedSpecificLike {
  action?: AnyActionSpecific | AnyActionSpecific[]
  condition?: AnyExpressionSpecific | boolean
  debounceTime?: AnyExpressionSpecific | number
  throttleTime?: AnyExpressionSpecific | number
  type: 'Event'
}

@scope({
  type: 'Block',
  variables() {
    const parameters = this.getParametersSchema()
    return parameters.reduce((acc: TypedVariableDeclaration[], parameter) => {
      const name = parameter.name
      if (!name) return acc
      return acc.concat({
        ...parameter,
        get() {
          return this.getSelfViewScope().getLocalVariable(name)
        },
        group: 'Event',
        name,
        schema: parameter,
        type: 'Local'
      })
    }, [])
  }
})
export class EventNode extends TypedNode {
  __r_event_maps: TRecord<(...args: any[]) => any> = {}

  @fromSpecific()
  @reactive('shallow')
  action?: AnyActionNode | AnyActionNode[] | MustacheNode | MustacheNode[]

  @fromSpecific()
  @reactive()
  condition?: boolean | MustacheNode

  @fromSpecific()
  @reactive()
  debounceTime?: MustacheNode | number

  @fromSpecific()
  @reactive()
  throttleTime?: MustacheNode | number

  @reactive('computed')
  get plugin() {
    return this.getPlugin<EventPlugin>()
  }

  getDebounceThrottleOptions(options: PromiseDebounceThrottleOptions) {
    return options
  }

  getEventSchema() {
    const schema = this.getEntityParent()?.getTypedSchema(
      this.getEntityBelongs()
    )
    if (schema && schema.type === 'Schema.Event') {
      return schema
    }
  }

  getParametersSchema() {
    return this.getEventSchema()?.parameters ?? []
  }

  /**
   * 是否应该触发事件，如果 condition 节点是表达式节点，但是表达式为空字符串，则认为应该触发
   * 其他情况根据表达式编译结果判断，true 或者 undefined 表示应该触发，false 表示不应该触发
   */
  async isShouldTrigger(result: EventResult): Promise<boolean> {
    const node = this.condition
    if (node instanceof MustacheNode && isEmpty(node.code)) return true
    return result.condition !== false
  }

  transformParameters(args: any[]) {
    const parameters = this.getParametersSchema()
    return parameters.reduce((acc: TRecord, parameter, index) => {
      if (parameter.name) {
        acc[parameter.name] = args[index] ?? (parameter as any).value
      }
      return acc
    }, {})
  }

  override transpile() {
    if (toArr(this.action).length === 0) return

    const debounceTime = this.evaluate(['debounceTime'])
    const throttleTime = this.evaluate(['throttleTime'])
    const eventKey = `${this.__r_indexes}#${debounceTime}#${throttleTime}`
    const plugin = this.plugin
    if (plugin.props.preBuildMockParameters) {
      nextTick(() => {
        this.getSelfViewScope()?.setLocalVariables(this.transformParameters([]))
      })
    }
    this.__r_event_maps[eventKey] ??= pDebounceThrottle(
      async (...args: any[]) => {
        const scope = this.getSelfViewScope()!
        const params = this.transformParameters(args)
        scope.setLocalVariables(params)
        const result = super.transpile<EventResult>(scope)
        const shouldTrigger = await this.isShouldTrigger(result)
        if (!shouldTrigger) return
        this.dispatchEvent(new EventTriggerEvent(params))
        await Promise.all(
          toArr(result.action)
            .filter((action) => typeof action === 'function')
            .map((action) => action())
        )
      },
      this.getDebounceThrottleOptions({ debounceTime, throttleTime })
    )

    return this.__r_event_maps[eventKey]
  }
}

declare global {
  export interface RehajeSpecifics {
    Event: EventSpecific
  }
  export interface RehajeTypedNodes {
    Event: EventNode
  }
}
